// src/modules/orders/utils/orderUtils.ts

export interface OrderItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  nutrition: {
    calories: number;
    proteins: number;
    fats: number;
    carbs: number;
  };
  measure: {
    value: number;
    unit: string;
  };
}

// ============================================
// 🎁 НОВОЕ: Интерфейс для подарков
// ============================================
export interface GiftItem {
  menu_item_id: number;
  name: string;
  article?: string;
  quantity: number;
  price: number;
  is_gift: boolean;
  ambassador_code?: string;
}
// ============================================

export interface MealPlan {
  [day: string]: {
    breakfast: OrderItem[];
    lunch: OrderItem[];
    dinner: OrderItem[];
  };
}

export interface GroupedOrderItem {
  id: number;
  name: string;
  price: number;
  totalQuantity: number;
  totalPrice: number;
  measure: {
    value: number;
    unit: string;
  };
}

// ============================================
// 🎁 НОВОЕ: Интерфейс для сгруппированных подарков
// ============================================
export interface GroupedGiftItem {
  id: number;
  name: string;
  price: number;
  totalQuantity: number;
  totalPrice: number;
  is_gift: boolean;
  ambassador_code?: string;
}
// ============================================

export interface GroupedMeals {
  breakfast: GroupedOrderItem[];
  lunch: GroupedOrderItem[];
  dinner: GroupedOrderItem[];
  gifts: GroupedGiftItem[];  // 🎁 НОВОЕ
  totalPrice: number;
  totalItems: number;
}

// Форматирование чисел с пробелами как разделителями разрядов
export const formatPrice = (price: number): string => {
  return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Группировка позиций по ID и названию
export const groupOrderItems = (items: OrderItem[]): GroupedOrderItem[] => {
  const grouped = items.reduce((acc, item) => {
    const key = `${item.id}-${item.name}`;
    
    if (acc[key]) {
      acc[key].totalQuantity += item.quantity;
      acc[key].totalPrice += item.price * item.quantity;
    } else {
      acc[key] = {
        id: item.id,
        name: item.name,
        price: item.price,
        totalQuantity: item.quantity,
        totalPrice: item.price * item.quantity,
        measure: item.measure,
      };
    }
    
    return acc;
  }, {} as Record<string, GroupedOrderItem>);

  return Object.values(grouped);
};

// ============================================
// 🎁 НОВОЕ: Группировка подарков
// ============================================
export const groupGiftItems = (gifts: GiftItem[]): GroupedGiftItem[] => {
  const grouped = gifts.reduce((acc, gift) => {
    const key = `${gift.menu_item_id}-${gift.name}`;
    
    if (acc[key]) {
      acc[key].totalQuantity += gift.quantity;
      acc[key].totalPrice += gift.price * gift.quantity;
    } else {
      acc[key] = {
        id: gift.menu_item_id,
        name: gift.name,
        price: gift.price,
        totalQuantity: gift.quantity,
        totalPrice: gift.price * gift.quantity,
        is_gift: true,
        ambassador_code: gift.ambassador_code,
      };
    }
    
    return acc;
  }, {} as Record<string, GroupedGiftItem>);

  return Object.values(grouped);
};
// ============================================

// Обработка данных заказа и группировка по приемам пищи
export const processOrderData = (orderData: any): GroupedMeals => {
  if (!orderData || typeof orderData !== 'object') {
    return {
      breakfast: [],
      lunch: [],
      dinner: [],
      gifts: [],  // 🎁 НОВОЕ
      totalPrice: 0,
      totalItems: 0,
    };
  }

  const allBreakfast: OrderItem[] = [];
  const allLunch: OrderItem[] = [];
  const allDinner: OrderItem[] = [];
  const allGifts: GiftItem[] = [];  // 🎁 НОВОЕ

  // ============================================
  // 🎁 НОВОЕ: Извлекаем подарки
  // ============================================
  if (orderData.gift_items && Array.isArray(orderData.gift_items)) {
    allGifts.push(...orderData.gift_items);
  }
  // ============================================

  // Собираем все позиции из всех дней
  Object.keys(orderData).forEach(day => {
    // Пропускаем gift_items (обработали выше)
    if (day === 'gift_items') {
      return;
    }

    const dayData = orderData[day];
    if (dayData && typeof dayData === 'object') {
      if (Array.isArray(dayData.breakfast)) {
        allBreakfast.push(...dayData.breakfast);
      }
      if (Array.isArray(dayData.lunch)) {
        allLunch.push(...dayData.lunch);
      }
      if (Array.isArray(dayData.dinner)) {
        allDinner.push(...dayData.dinner);
      }
    }
  });

  // Группируем позиции по приемам пищи
  const groupedBreakfast = groupOrderItems(allBreakfast);
  const groupedLunch = groupOrderItems(allLunch);
  const groupedDinner = groupOrderItems(allDinner);
  const groupedGifts = groupGiftItems(allGifts);  // 🎁 НОВОЕ

  // Считаем общие показатели
  const totalPrice = [
    ...groupedBreakfast,
    ...groupedLunch,
    ...groupedDinner,
  ].reduce((sum, item) => sum + item.totalPrice, 0);

  const totalItems = [
    ...groupedBreakfast,
    ...groupedLunch,
    ...groupedDinner,
    ...groupedGifts,  // 🎁 НОВОЕ: включаем подарки в общий счет
  ].reduce((sum, item) => sum + item.totalQuantity, 0);

  return {
    breakfast: groupedBreakfast,
    lunch: groupedLunch,
    dinner: groupedDinner,
    gifts: groupedGifts,  // 🎁 НОВОЕ
    totalPrice,
    totalItems,
  };
};

// Получение названий приемов пищи
export const getMealNames = () => ({
  breakfast: 'Завтрак',
  lunch: 'Обед',
  dinner: 'Ужин',
  gifts: 'Подарки',  // 🎁 НОВОЕ
});

// Форматирование строки позиции для отображения
export const formatOrderItemLine = (item: GroupedOrderItem): string => {
  const unitText = `${item.measure.value}${item.measure.unit}`;
  const priceFormatted = formatPrice(item.price);
  const totalPriceFormatted = formatPrice(item.totalPrice);
  
  return `- ${item.name} (${unitText}) — ${item.totalQuantity} шт. × ${priceFormatted}₸ = ${totalPriceFormatted}₸`;
};

// ============================================
// 🎁 НОВОЕ: Форматирование подарков
// ============================================
export const formatGiftItemLine = (gift: GroupedGiftItem): string => {
  const priceFormatted = formatPrice(gift.price);
  const ambassadorInfo = gift.ambassador_code ? ` (промокод: ${gift.ambassador_code})` : '';
  
  return `🎁 ${gift.name} — ${gift.totalQuantity} шт. × ${priceFormatted}₸ = БЕСПЛАТНО${ambassadorInfo}`;
};
// ============================================